/* eslint jsx-a11y/label-has-associated-control: ["error", { assert: "either", depth: 25 }] */
import React, { useState, useEffect, useMemo } from 'react';
import {
  Modal,
} from 'flowbite-react';
import { BarWave } from 'react-cssfx-loading';
import { useNavigate } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faSearch,
  faEdit, faTrash, faFilePdf, faFileWord, faChartBar, faCalendarAlt, faEye,
} from '@fortawesome/free-solid-svg-icons';
import { useGetPublicationsQuery, useDeletePublicationMutation } from '../../../../redux/apiSlice';
import Pagination from '../../../components/Pagination/Pagination';

const PageSize = 10;
const PublishedPublications = () => {
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [currentPublicId, setCurrentPublicId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredPublications, setfilteredPublications] = useState([]);
  const [deletionStatus, setDeletionStatus] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  const { data: publications, error, refetch } = useGetPublicationsQuery();
  const navigate = useNavigate();

  useEffect(() => {
    if (publications) {
      setfilteredPublications(publications);
    }
  }, [publications]);

  useEffect(() => {
    if (publications) {
      refetch();
      const query = searchQuery.toLowerCase() || '';
      setfilteredPublications(
        publications.filter((publication) => publication.post_title.toLowerCase().includes(query)
          || publication.name.toLowerCase().includes(query)
          || publication.post_type.toLowerCase().includes(query)
          || publication.post_content.toLowerCase().includes(query)
          || publication.post_date.toLowerCase().includes(query)),
      );
    }
  }, [searchQuery, publications, refetch]);

  const [deletePublication] = useDeletePublicationMutation();

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleDeletePublication = async (publicationId) => {
    setCurrentPublicId(publicationId);
    setOpenDeleteModal(true);
  };

  const confirmDeletePublication = async () => {
    try {
      await deletePublication(currentPublicId);
      setSuccessMsg('Publication deleted Successfully.');
      setTimeout(() => {
        setSuccessMsg('');
      }, 2000);
      setDeletionStatus(true);
      setOpenDeleteModal(false);
    } catch (error) {
      console.log('error deleting publication', error);
    }
  };

  useEffect(() => {
    if (deletionStatus) {
      refetch();
      setDeletionStatus(false);
    }
  }, [deletionStatus, refetch]);

  if (error) {
    console.error('Error fetching publications', error);
  }

  const paginatedPublications = useMemo(() => {
    const firstPageIndex = (currentPage - 1) * PageSize;
    const lastPageIndex = firstPageIndex + PageSize;
    return filteredPublications.slice(firstPageIndex, lastPageIndex);
  }, [currentPage, filteredPublications]);

  // Calculate statistics
  const totalPublications = publications?.length || 0;
  const pdfPublications = publications?.filter((pub) => pub.guid?.includes('.pdf'))?.length || 0;
  const wordPublications = publications?.filter((pub) => pub.guid?.includes('.doc'))?.length || 0;
  const recentPublications = publications?.slice(0, 5) || [];
  const thisMonthPublications = publications?.filter((publication) => {
    const publicationDate = new Date(publication.post_date);
    const now = new Date();
    return (
      publicationDate.getMonth() === now.getMonth()
      && publicationDate.getFullYear() === now.getFullYear()
    );
  })?.length || 0;

  // Find most viewed publication
  const mostViewedPublication = publications ? [...publications].sort((a, b) => {
    const aViews = a.view_count || 0;
    const bViews = b.view_count || 0;
    if (aViews !== bViews) {
      return bViews - aViews;
    }
    return new Date(b.post_date) - new Date(a.post_date);
  })[0] : null;

  if (!publications) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <BarWave color="#6366f1" height="2rem" width="8rem" />
          <p className="mt-4 text-gray-300">Loading publications...</p>
        </div>
      </div>
    );
  }

  const getFileIcon = (guid) => {
    const fileExtension = guid.slice(guid.lastIndexOf('.'));
    return fileExtension === '.pdf' ? faFilePdf : faFileWord;
  };

  return (
    <div className="p-6 w-full min-h-screen bg-gray-900">
      <div className="flex gap-6">
        {/* Main Content */}
        <div className="flex-1">
          <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 overflow-hidden">
            {/* Header Section */}
            <div className="bg-gray-700 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-green-600 rounded-lg">
                    <FontAwesomeIcon icon={faFilePdf} className="text-white text-lg" />
                  </div>
                  <div>
                    <h1 className="text-xl font-semibold text-white">Publication Management</h1>
                    <p className="text-gray-300 text-sm">Manage SMS publications and official documents</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => navigate('/private/publications-form')}
                  className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                >
                  <FontAwesomeIcon icon={faPlus} className="text-sm" />
                  <span className="font-medium">Create Publication</span>
                </button>
              </div>
            </div>

            {/* Search Section */}
            <div className="px-6 py-4 bg-gray-750 border-b border-gray-600">
              <div className="flex items-center justify-between">
                <div className="flex-1 max-w-md">
                  <div className="relative">
                    <FontAwesomeIcon
                      icon={faSearch}
                      className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                    />
                    <input
                      type="text"
                      placeholder="Search publications..."
                      value={searchQuery}
                      onChange={handleSearch}
                      className="w-full pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-white placeholder-gray-400"
                    />
                  </div>
                </div>
                <div className="ml-4 text-sm text-gray-300">
                  {filteredPublications.length}
                  {' '}
                  publication
                  {filteredPublications.length !== 1 ? 's' : ''}
                  {' '}
                  found
                </div>
              </div>
            </div>

            {/* Success Message */}
            {successMsg && (
              <div className="mx-6 mt-4 bg-green-900/20 border border-green-700 text-green-300 px-4 py-3 rounded-lg">
                <p className="text-center">{successMsg}</p>
              </div>
            )}

            {/* Table Section */}
            {filteredPublications && filteredPublications.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-750">
                    <tr className="border-b border-gray-600">
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Title
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Author
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                        File Type
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-300 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-gray-800 divide-y divide-gray-700">
                    {paginatedPublications.map(({
                      id, postTitle, name, postType, guid, postDate,
                    }, index) => (
                      <tr key={id} className={`hover:bg-gray-750 ${index % 2 === 0 ? 'bg-gray-800' : 'bg-gray-750'}`}>
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-white">
                            {postTitle}
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-sm text-white">{name}</div>
                        </td>
                        <td className="px-4 py-4">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-900 text-green-200">
                            {postType}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center space-x-2">
                            <FontAwesomeIcon
                              icon={getFileIcon(guid)}
                              className={`text-lg ${guid.includes('.pdf') ? 'text-red-400' : 'text-blue-400'}`}
                            />
                            <span className="text-sm text-gray-300">
                              {guid.slice(guid.lastIndexOf('.')).toUpperCase()}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-sm text-gray-300">
                            {new Date(postDate).toLocaleDateString('en-UK', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                            })}
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center justify-center space-x-2">
                            <button
                              type="button"
                              onClick={() => navigate(`/private/editPublication/${id}`)}
                              className="p-2 text-green-400 hover:text-green-300 hover:bg-green-900/20 rounded-lg transition-all duration-200"
                              title="Edit Publication"
                            >
                              <FontAwesomeIcon icon={faEdit} className="text-sm" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeletePublication(id)}
                              className="p-2 text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded-lg transition-all duration-200"
                              title="Delete Publication"
                            >
                              <FontAwesomeIcon icon={faTrash} className="text-sm" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FontAwesomeIcon icon={faFilePdf} className="text-2xl text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-white mb-2">No publications found</h3>
                <p className="text-gray-400 mb-6">Get started by creating your first publication</p>
                <button
                  type="button"
                  onClick={() => navigate('/private/publications-form')}
                  className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                >
                  <FontAwesomeIcon icon={faPlus} className="mr-2" />
                  Create First Publication
                </button>
              </div>
            )}

            {/* Pagination */}
            {filteredPublications && filteredPublications.length > 0 && (
              <div className="px-6 py-4 bg-gray-750 border-t border-gray-600">
                <Pagination
                  itemsDisplaying={paginatedPublications.length}
                  itemsPerPage={PageSize}
                  items={filteredPublications}
                  handleNext={() => setCurrentPage(currentPage + 1)}
                  handlePrevious={() => setCurrentPage(currentPage - 1)}
                />
              </div>
            )}
          </div>
        </div>

        {/* Right Sidebar - Publication Statistics */}
        <div className="w-80 space-y-6">
          {/* Statistics Overview */}
          <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 p-6">
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-2 bg-green-600 rounded-lg">
                <FontAwesomeIcon icon={faChartBar} className="text-white text-lg" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Publication Statistics</h3>
                <p className="text-gray-300 text-sm">Overview of your documents</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-gray-750 rounded-lg">
                <div className="flex items-center space-x-3">
                  <FontAwesomeIcon icon={faFilePdf} className="text-green-400" />
                  <span className="text-gray-300">Total Publications</span>
                </div>
                <span className="text-2xl font-bold text-white">{totalPublications}</span>
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-750 rounded-lg">
                <div className="flex items-center space-x-3">
                  <FontAwesomeIcon icon={faFilePdf} className="text-red-400" />
                  <span className="text-gray-300">PDF Files</span>
                </div>
                <span className="text-2xl font-bold text-red-400">{pdfPublications}</span>
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-750 rounded-lg">
                <div className="flex items-center space-x-3">
                  <FontAwesomeIcon icon={faFileWord} className="text-blue-400" />
                  <span className="text-gray-300">Word Files</span>
                </div>
                <span className="text-2xl font-bold text-blue-400">{wordPublications}</span>
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-750 rounded-lg">
                <div className="flex items-center space-x-3">
                  <FontAwesomeIcon icon={faCalendarAlt} className="text-purple-400" />
                  <span className="text-gray-300">This Month</span>
                </div>
                <span className="text-2xl font-bold text-purple-400">{thisMonthPublications}</span>
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-750 rounded-lg">
                <div className="flex items-center space-x-3">
                  <FontAwesomeIcon icon={faEye} className="text-orange-400" />
                  <span className="text-gray-300">Most Viewed</span>
                </div>
                <div className="text-right">
                  <div className="text-sm font-medium text-white truncate max-w-24">
                    {mostViewedPublication?.post_title || 'N/A'}
                  </div>
                  <div className="text-xs text-gray-400">
                    {mostViewedPublication?.view_count || 0}
                    {' '}
                    views
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Recent Publications */}
          <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 p-6">
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-2 bg-green-600 rounded-lg">
                <FontAwesomeIcon icon={faEye} className="text-white text-lg" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Recent Publications</h3>
                <p className="text-gray-300 text-sm">Latest uploaded documents</p>
              </div>
            </div>

            <div className="space-y-3">
              {recentPublications.map((publication) => (
                <div key={publication.id} className="p-3 bg-gray-750 rounded-lg hover:bg-gray-700 transition-colors cursor-pointer">
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-medium text-white truncate">
                        {publication.post_title}
                      </h4>
                      <p className="text-xs text-gray-400 mt-1">
                        {new Date(publication.post_date).toLocaleDateString('en-UK', {
                          month: 'short',
                          day: 'numeric',
                        })}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 p-6">
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-2 bg-purple-600 rounded-lg">
                <FontAwesomeIcon icon={faPlus} className="text-white text-lg" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Quick Actions</h3>
                <p className="text-gray-300 text-sm">Common tasks</p>
              </div>
            </div>

            <div className="space-y-3">
              <button
                type="button"
                onClick={() => navigate('/private/publications-form')}
                className="w-full flex items-center space-x-3 p-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <FontAwesomeIcon icon={faPlus} className="text-white" />
                <span className="text-white font-medium">Create New Publication</span>
              </button>

              <button
                type="button"
                onClick={() => navigate('/private/publications')}
                className="w-full flex items-center space-x-3 p-3 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
              >
                <FontAwesomeIcon icon={faFilePdf} className="text-gray-300" />
                <span className="text-gray-300 font-medium">View All Publications</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Modal */}
      <Modal show={openDeleteModal} size="md" onClose={() => setOpenDeleteModal(false)} popup>
        <Modal.Header className="bg-gray-700 border-b border-gray-600">
          <h3 className="text-lg font-semibold text-white">Delete Publication</h3>
        </Modal.Header>
        <Modal.Body className="bg-gray-800">
          <div className="text-center p-6">
            <div className="w-16 h-16 bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <FontAwesomeIcon icon={faTrash} className="text-2xl text-red-400" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">Confirm Deletion</h3>
            <p className="text-gray-300 mb-6">Are you sure you want to delete this publication? This action cannot be undone.</p>
            <div className="flex items-center justify-center space-x-4">
              <button
                type="button"
                onClick={() => setOpenDeleteModal(false)}
                className="px-4 py-2 text-gray-300 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDeletePublication}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete Publication
              </button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default PublishedPublications;
